import { TaskLinks } from "@coworker/app/src/components/RoomSettings/types/taskLink";
import { hashKeys } from "@coworker/app/src/components/RoomSettings/tools";
import {
  duplicateTasksToParent,
  filterOngoingAndRecentTasks,
} from "@coworker/app/src/components/RoomSettings/utils/taskUtils";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { httpClient } from "@coworker/http-client";
import { MediaTask } from "@coworker/types/lib/tasks/media";
import { useQuery } from "@tanstack/react-query";

const fetchV1TasksByIds = async (taskIds: string[]): Promise<MediaTask[]> => {
  const SLICE_SIZE = 200;
  if (taskIds.length === 0) return [];

  const batchedTaskIds: string[][] = [];
  for (let i = 0; i < taskIds.length; i += SLICE_SIZE) {
    batchedTaskIds.push(taskIds.slice(i, i + SLICE_SIZE));
  }

  const allTasks: MediaTask[] = [];
  for (const batch of batchedTaskIds) {
    const result = await httpClient.tasks.get<{ tasks: MediaTask[] }>(
      "/v1/tasks/by_ids",
      { params: { ids: batch.join(",") } }
    );
    const responseTasks: MediaTask[] = result?.tasks || [];
    allTasks.push(...responseTasks);
  }

  return allTasks;
};

const fetchTasksByIds = async (
  taskIds: string[],
  taskLinks: TaskLinks,
  applyOngoingAndRecentFilter = true
): Promise<MediaTask[]> => {
  if (taskIds.length === 0) return [];

  // Fetch v1 tasks only
  const v1Tasks = await fetchV1TasksByIds(taskIds);

  const updatedTasks = duplicateTasksToParent(v1Tasks, taskLinks);
  return applyOngoingAndRecentFilter
    ? filterOngoingAndRecentTasks(updatedTasks)
    : updatedTasks;
};

export const useTasksByIds = (
  taskIds: string[],
  taskLinks: TaskLinks,
  applyOngoingAndRecentFilter = true
) => {
  const storeId = useStoreId();

  return useQuery({
    queryKey: [
      "tasks",
      hashKeys(taskIds),
      hashKeys(
        taskLinks.map(
          (tl) =>
            `${tl.taskId}:${tl.mediaId}:${tl.parentMediaId}:${tl.updatedAt}`
        )
      ),
      storeId,
      applyOngoingAndRecentFilter,
    ],
    queryFn: async () =>
      await fetchTasksByIds(taskIds, taskLinks, applyOngoingAndRecentFilter),
    enabled: taskIds.length > 0 && taskLinks.length > 0,
  });
};
